'use client';

import { useState } from 'react';

import R2Image from '@/components/ui/R2Image';
import PrimaryButton from '@/components/ui/Button/PrimaryButton';
import RichText from '@/components/ui/RichText/RichText';
import type {
  HowItWorksCard,
  HowItWorksSectionData,
} from '@/features/gastricAlimetry/data/gastric-alimetry.types';

const createCollapsedMarkdown = (content: string) => {
  let visibleBulletCount = 0;

  return content
    .split('\n')
    .filter((line) => {
      if (!/^\s*[-*+]\s+/.test(line)) return true;
      visibleBulletCount += 1;
      return visibleBulletCount === 1;
    })
    .join('\n')
    .trimEnd();
};

const HowItWorksCardItem = ({ card }: { card: HowItWorksCard }) => {
  const [expanded, setExpanded] = useState(false);
  const collapsedContent = createCollapsedMarkdown(card.description.content);
  const hasHiddenContent = collapsedContent !== card.description.content;

  return (
    <div className="flex flex-col rounded-sm bg-white p-7 shadow-[0_12px_24px_rgba(0,0,0,0.18)]">
      <R2Image
        path={card.imagePath}
        alt={card.imageAlt}
        containerClassName="relative w-full aspect-4/3 overflow-hidden rounded-lg"
        className="object-cover"
      />

      <div className="mt-4">
        <RichText styles={card.description.styles}>
          {expanded ? card.description.content : collapsedContent}
        </RichText>

        {hasHiddenContent && (
          <div className="flex justify-end">
            <button
              type="button"
              aria-expanded={expanded}
              onClick={() => setExpanded((current) => !current)}
              className="text-text-default mt-2 cursor-pointer text-sm font-normal underline"
            >
              {expanded ? 'View Less' : 'View More'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

type Props = {
  data: HowItWorksSectionData;
};

const GastricAlimetryHowItWorksSection = ({ data }: Props) => {
  return (
    <section className="content-wrapper py-8 md:py-10">
      <RichText styles={data.heading.styles}>{data.heading.content}</RichText>

      <div className="mt-10 grid grid-cols-1 gap-8 md:grid-cols-3">
        {data.cards.map((card) => (
          <HowItWorksCardItem key={card.imagePath} card={card} />
        ))}
      </div>

      <div className={data.buttonContainerClassName}>
        <PrimaryButton data={data.button} />
      </div>
    </section>
  );
};

export default GastricAlimetryHowItWorksSection;
